const API_BASE = process.env.REACT_APP_FUNC_ENDPOINT || "";

export interface PlanMeta {
  id: string;
  planId: string;
  title: string;
  description: string;
  ownerEmail: string;
  editMode: "everyone" | "restricted";
  editors: string[];
  settings: PlanSettings;
  accessId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanSettings {
  columnVisibility: {
    assigned_to: boolean;
    start_date: boolean;
    duration: boolean;
    status: boolean;
  };
}

export interface PlanTask {
  id: string;
  planId: string;
  type: "task";
  text: string;
  taskType: string;
  parentId: string | null;
  color: string | null;
  status: string;
  assigned_to: string;
  start_date: string | null;
  end_date: string | null;
  duration: number | null;
  progress: number;
  order: number;
  open: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanLink {
  id: string;
  planId: string;
  type: "link";
  source: string;
  target: string;
  linkType: string;
  createdAt: string;
}

export interface PlanMarker {
  id: string;
  planId: string;
  type: "marker";
  text: string;
  start_date: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface FullPlan {
  plan: PlanMeta;
  tasks: PlanTask[];
  links: PlanLink[];
  markers: PlanMarker[];
  canEdit: boolean;
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const url = `${API_BASE}${path}`;
  const token = localStorage.getItem("gantt-auth-token") || "";

  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}

// ============================================
// Plans
// ============================================

export async function listPlans(contextId: string): Promise<PlanMeta[]> {
  const res = await apiFetch(`/api/plans?contextId=${encodeURIComponent(contextId)}`);
  if (!res.ok) throw new Error("Failed to list plans");
  return res.json();
}

export async function createPlan(data: {
  title: string;
  contextId: string;
  contextType?: string;
  teamId?: string;
  channelId?: string;
  chatId?: string;
}): Promise<PlanMeta> {
  const res = await apiFetch("/api/plans", {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create plan");
  return res.json();
}

export async function getFullPlan(planId: string): Promise<FullPlan> {
  const res = await apiFetch(`/api/plans/${planId}/full`);
  if (!res.ok) throw new Error("Failed to get plan");
  return res.json();
}

export async function syncPlan(planId: string, lastModified: string): Promise<FullPlan | null> {
  const res = await apiFetch(`/api/plans/${planId}/sync?lastModified=${encodeURIComponent(lastModified)}`);
  if (res.status === 304) return null;
  if (!res.ok) throw new Error("Failed to sync plan");
  return res.json();
}

export async function updatePlan(planId: string, data: Partial<PlanMeta>): Promise<PlanMeta> {
  const res = await apiFetch(`/api/plans/${planId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update plan");
  return res.json();
}

export async function deletePlan(planId: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete plan");
}

// ============================================
// Tasks
// ============================================

export async function createTask(planId: string, data: Partial<PlanTask>): Promise<PlanTask> {
  const res = await apiFetch(`/api/plans/${planId}/tasks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create task");
  return res.json();
}

export async function updateTask(planId: string, taskId: string, data: Partial<PlanTask>): Promise<PlanTask> {
  const res = await apiFetch(`/api/plans/${planId}/tasks/${taskId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update task");
  return res.json();
}

export async function deleteTask(planId: string, taskId: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}/tasks/${taskId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete task");
}

// ============================================
// Links
// ============================================

export async function createLink(planId: string, data: { source: string; target: string; linkType?: string }): Promise<PlanLink> {
  const res = await apiFetch(`/api/plans/${planId}/links`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create link");
  return res.json();
}

export async function deleteLink(planId: string, linkId: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}/links/${linkId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete link");
}

// ============================================
// Markers
// ============================================

export async function createMarker(planId: string, data: Partial<PlanMarker>): Promise<PlanMarker> {
  const res = await apiFetch(`/api/plans/${planId}/markers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create marker");
  return res.json();
}

export async function updateMarker(planId: string, markerId: string, data: Partial<PlanMarker>): Promise<PlanMarker> {
  const res = await apiFetch(`/api/plans/${planId}/markers/${markerId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update marker");
  return res.json();
}

export async function deleteMarker(planId: string, markerId: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}/markers/${markerId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete marker");
}

// ============================================
// Access
// ============================================

export async function createAccess(data: {
  planId: string;
  contextId: string;
  contextType?: string;
  teamId?: string;
  channelId?: string;
  chatId?: string;
  userId?: string;
}): Promise<any> {
  const res = await apiFetch("/api/access", {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create access binding");
  return res.json();
}

export async function deleteAccess(accessId: string, contextId: string): Promise<void> {
  const res = await apiFetch(`/api/access/${accessId}?contextId=${encodeURIComponent(contextId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete access binding");
}

// ============================================
// Sharing
// ============================================

export async function getShareCode(planId: string): Promise<string> {
  const res = await apiFetch(`/api/plans/${planId}/share`, { method: "POST" });
  if (!res.ok) throw new Error("Failed to get share code");
  const data = await res.json();
  return data.shareCode;
}

export async function joinPlan(shareCode: string, contextId: string): Promise<{ plan: { id: string; title: string }; alreadyJoined: boolean }> {
  const res = await apiFetch("/api/plans/join", {
    method: "POST",
    body: JSON.stringify({ shareCode, contextId }),
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error("Invalid share code");
    throw new Error("Failed to join plan");
  }
  return res.json();
}

export async function shareWithEmail(planId: string, email: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}/share-with`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error("Failed to share with user");
}

export async function checkPendingShares(email: string, contextId: string): Promise<string[]> {
  const res = await apiFetch("/api/plans/check-shares", {
    method: "POST",
    body: JSON.stringify({ email, contextId }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.joined || [];
}

// ============================================
// Attachments
// ============================================

export interface Attachment {
  id: string;
  planId: string;
  taskId: string;
  type: "attachment";
  name: string;
  size: number;
  mimeType: string;
  blobUrl: string;
  uploadedBy: string;
  uploadedAt: string;
}

export async function listAttachments(planId: string, taskId: string): Promise<Attachment[]> {
  const res = await apiFetch(`/api/plans/${planId}/tasks/${taskId}/attachments`);
  if (!res.ok) throw new Error("Failed to list attachments");
  return res.json();
}

export async function uploadAttachment(planId: string, taskId: string, file: File): Promise<Attachment> {
  const fileData = await fileToBase64(file);
  const res = await apiFetch(`/api/plans/${planId}/tasks/${taskId}/attachments`, {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      fileData,
      fileType: file.type,
    }),
  });
  if (!res.ok) throw new Error("Failed to upload attachment");
  return res.json();
}

export async function deleteAttachment(planId: string, taskId: string, attachmentId: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${planId}/tasks/${taskId}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete attachment");
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]); // strip data:...;base64, prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
